import { useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useGlobalSearchStore } from "src/features/globalSearch/stores/GlobalSearchStore";

const isMac = (): boolean => {
  return navigator.platform.toUpperCase().indexOf("MAC") >= 0;
};

/**
 * Global keyboard shortcuts for AtomAssist chat pages.
 *
 * - Ctrl/Cmd+L → navigate to /assistant (new chat)
 * - Shift+Escape → focus the chat input textarea
 */
export const useChatKeyboardShortcuts = () => {
  const history = useHistory();
  const { isOpen: isSearchOpen } = useGlobalSearchStore();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't intercept when global search modal is open
      if (isSearchOpen) return;

      // Ctrl/Cmd + L → new chat
      const isModifierKey = isMac() ? event.metaKey : event.ctrlKey;
      if (isModifierKey && event.key.toLowerCase() === "l") {
        event.preventDefault();
        event.stopPropagation();
        history.push("/assistant");
        return;
      }

      // Shift+Escape → focus chat input
      if (event.key === "Escape" && event.shiftKey) {
        event.preventDefault();
        const textarea = document.querySelector(
          '[data-testid="aa-input-textarea"] textarea',
        ) as HTMLElement;
        textarea?.focus();
        return;
      }
    },
    [isSearchOpen, history],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [handleKeyDown]);
};
